"use client"

import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { useMediaQuery } from "@/hooks/useMediaQuery"
import { PropsWithChildren, useState } from "react"
import SupporterTable from "./SupporterTable"
import AvatarGroup from "@/app/components/Avatar/Group/AvatarGroup"
import { SupportersResp } from "@/types/coins"
import { User } from "@/types/users"

type Props = PropsWithChildren<{
  avatars: Partial<User>[]
  supporters: SupportersResp
  isSection?: boolean
  tier?: number
}>

export default function SupportersDialogDrawer({
  avatars,
  supporters,
  tier,
  isSection,
}: Props) {
  const [open, setOpen] = useState(false)
  const isDesktop = useMediaQuery("(min-width: 640px)")

  const total = supporters?.totalSupporterCount || 0

  if (total === 0 || !supporters) return null

  const text = isSection ? `${total}` : `${total} supporters`
  const title = getModalTitle(supporters, tier)

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger className="flex items-center">
          <AvatarGroup
            avatars={avatars}
            text={text}
            textClassName="pointer-events-none truncate"
            small
          />
        </DialogTrigger>
        <DialogContent>
          <DialogHeader className="mb-2">
            <DialogTitle className="font-header text-foreground text-2xl">
              {title}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[360px] overflow-auto">
            <SupporterTable supporters={supporters} />
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger className="flex items-center">
        <AvatarGroup
          avatars={avatars}
          text={text}
          textClassName="pointer-events-none truncate"
        />
      </DrawerTrigger>
      <DrawerContent className="px-2">
        <DrawerHeader className="px-4 pt-4 pb-2">
          <DrawerTitle className="font-header text-foreground text-xl text-left">
            {title}
          </DrawerTitle>
        </DrawerHeader>
        <div className="max-h-[360px] mx-auto w-full">
          <SupporterTable isMinimal supporters={supporters} />
        </div>
      </DrawerContent>
    </Drawer>
  )
}

function getModalTitle(supporters: SupportersResp, tier?: Number): string {
  switch (tier) {
    case 1:
      return `${supporters.totalSupporterCount} liked it`

    case 5:
      return `${supporters.totalSupporterCount} loved it`

    case 10:
      return `${supporters.totalSupporterCount} true believers`

    default:
      return `${supporters.totalSupporterCount} supporters`
  }
}
